import { IAnalysisNodeData, IResultsAndDefinitionsData, SecondaryNodeColor } from 'types';
import {
  Results1,
  Results2A,
  Results2B,
  Results3A,
  Results3B,
  Results4A,
  Results4B,
  Definitions3A,
  Definitions3B,
  Definitions3C,
  Definitions3D,
  Definitions1,
  Definitions2,
  Definitions4A,
  Definitions4B,
  Definitions4C,
} from 'assets';

export enum TargetVariableTitle {
  CUSTOMER_KNOWLEDGE_SHARING = 'customer knowledge sharing',
  SUGGESTION_SHARING_LIKELIHOOD = 'suggestion sharing likelihood',
  CUSTOMER_FEEDBACK = 'customer feedback',
  PROVIDING_FEEDBACK = 'providing feedback',
}

export enum AntecedentTitle {
  HELPING_OTHER_CUSTOMERS = 'helping other customers',
  SOCIAL_BOND = 'social bond',
  SUBJECTIVE_HAPPINESS = 'subjective happiness',
  RECIPROCITY = 'Reciprocity',
  SELF_BENEFIT = 'self benefit',
  FIRM_BENEFIT = 'firm benefit',
  OTHER_BENEFIT = 'other benefit',
  COMPETITION = 'competition',
  EXPORTS_DESTINATION = 'exports destination',
  CORPORATE_IMAGE = 'corporate image',
}

export enum ConsequenceTitle {
  INNOVATION = 'innovation',
  LONG_TERM_RELATIONSHIP_ORIENTED = 'long-term relationship oriented',
}

const resultsAndDefinitions: IResultsAndDefinitionsData[] = [
  {
    resultsData: [
      {
        image: Results1,
      },
    ],
    definitionsData: [
      {
        image: Definitions1,
      },
    ],
  },
  {
    resultsData: [
      {
        image: Results2A,
      },
      {
        image: Results2B,
      },
    ],
    definitionsData: [
      {
        image: Definitions2,
      },
    ],
  },
  {
    resultsData: [
      {
        image: Results3A,
      },
      {
        image: Results3B,
      },
    ],
    definitionsData: [
      {
        image: Definitions3A,
      },
      {
        image: Definitions3B,
      },
      {
        image: Definitions3C,
      },
      {
        image: Definitions3D,
      },
    ],
  },
  {
    resultsData: [
      {
        image: Results4A,
      },
      {
        image: Results4B,
      },
    ],
    definitionsData: [
      {
        image: Definitions4A,
      },
      {
        image: Definitions4B,
      },
      {
        image: Definitions4C,
      },
    ],
  },
];

type IAntecedentResultsData = {
  [key in AntecedentTitle]: IAnalysisNodeData;
};

export const antecedentResultsData: IAntecedentResultsData = {
  [AntecedentTitle.HELPING_OTHER_CUSTOMERS]: {
    tables: [
      {
        tableTitle: 'Antecedents',
        tableData: [
          {
            author: 'Baima et al., 2022',
            estimate: '0,075',
            pValue: '>0,05',
            color: SecondaryNodeColor.BLUE,
          },
        ],
      },
    ],
    ...resultsAndDefinitions[0],
  },
  [AntecedentTitle.SOCIAL_BOND]: {
    tables: [
      {
        tableTitle: 'Antecedents',
        tableData: [
          {
            author: 'Baima et al., 2022',
            estimate: '0,381',
            pValue: '<0,01',
            color: SecondaryNodeColor.GREEN,
          },
        ],
      },
    ],
    ...resultsAndDefinitions[0],
  },
  [AntecedentTitle.SUBJECTIVE_HAPPINESS]: {
    tables: [
      {
        tableTitle: 'Antecedents',
        tableData: [
          {
            author: 'Baima et al., 2022',
            estimate: '0,094',
            pValue: '<0,05',
            color: SecondaryNodeColor.GREEN,
          },
        ],
      },
    ],
    ...resultsAndDefinitions[0],
  },
  [AntecedentTitle.RECIPROCITY]: {
    tables: [
      {
        tableTitle: 'Antecedents',
        tableData: [
          {
            author: 'Baima et al., 2022',
            estimate: '0,237',
            pValue: '<0,05',
            color: SecondaryNodeColor.GREEN,
          },
        ],
      },
    ],
    ...resultsAndDefinitions[0],
  },
  [AntecedentTitle.SELF_BENEFIT]: {
    tables: [
      {
        tableTitle: 'Antecedents',
        tableData: [
          {
            author: 'Burnham et al., 2020',
            estimate: '3,49',
            pValue: '>0,1',
            color: SecondaryNodeColor.BLUE,
          },
        ],
      },
    ],
    resultsData: [resultsAndDefinitions[1].resultsData[1]],
    definitionsData: resultsAndDefinitions[1].definitionsData,
  },
  [AntecedentTitle.FIRM_BENEFIT]: {
    tables: [
      {
        tableTitle: 'Antecedents',
        tableData: [
          {
            author: 'Burnham et al., 2020',
            estimate: '4,6',
            pValue: '<0,05',
            color: SecondaryNodeColor.GREEN,
          },
          {
            author: 'Burnham et al., 2020',
            estimate: '1,95',
            pValue: '>0,1',
            color: SecondaryNodeColor.BLUE,
          },
          {
            author: 'Burnham et al., 2020',
            estimate: '-3,24',
            pValue: '>0,1',
            color: SecondaryNodeColor.BLUE,
          },
        ],
      },
    ],
    resultsData: [resultsAndDefinitions[1].resultsData[0]],
    definitionsData: resultsAndDefinitions[1].definitionsData,
  },
  [AntecedentTitle.OTHER_BENEFIT]: {
    tables: [
      {
        tableTitle: 'Antecedents',
        tableData: [
          {
            author: 'Burnham et al., 2020',
            estimate: '5,53',
            pValue: '<0,01',
            color: SecondaryNodeColor.GREEN,
          },
        ],
      },
    ],
    resultsData: [resultsAndDefinitions[1].resultsData[1]],
    definitionsData: resultsAndDefinitions[1].definitionsData,
  },
  [AntecedentTitle.COMPETITION]: {
    tables: [
      {
        tableTitle: 'Antecedents',
        tableData: [
          {
            author: 'Barasa et al., 2020',
            estimate: '-0,386',
            pValue: '<0,01',
            color: SecondaryNodeColor.RED,
          },
        ],
      },
    ],
    resultsData: [resultsAndDefinitions[2].resultsData[1]],
    definitionsData: [
      resultsAndDefinitions[2].definitionsData[1],
      resultsAndDefinitions[2].definitionsData[2],
    ],
  },
  [AntecedentTitle.EXPORTS_DESTINATION]: {
    tables: [
      {
        tableTitle: 'Antecedents',
        tableData: [
          {
            author: 'Barasa et al., 2020',
            estimate: '1,164',
            pValue: '<0,01',
            color: SecondaryNodeColor.GREEN,
          },
        ],
      },
    ],
    resultsData: [resultsAndDefinitions[2].resultsData[1]],
    definitionsData: [
      resultsAndDefinitions[2].definitionsData[2],
      resultsAndDefinitions[2].definitionsData[3],
    ],
  },
  [AntecedentTitle.CORPORATE_IMAGE]: {
    tables: [
      {
        tableTitle: 'Antecedents',
        tableData: [
          {
            author: 'Kim, Yin, Lee, 2020',
            estimate: '0,68',
            pValue: '<0,05',
            color: SecondaryNodeColor.GREEN,
          },
        ],
      },
    ],
    resultsData: [resultsAndDefinitions[3].resultsData[0]],
    definitionsData: [
      resultsAndDefinitions[3].definitionsData[0],
      resultsAndDefinitions[3].definitionsData[1],
    ],
  },
};

export type IConsequenceResultsData = {
  [key in ConsequenceTitle]: IAnalysisNodeData;
};

export const consequenceResultsData: IConsequenceResultsData = {
  [ConsequenceTitle.INNOVATION]: {
    tables: [
      {
        tableTitle: 'Consequences',
        tableData: [
          {
            author: 'Barasa et al., 2020',
            estimate: '0,392',
            pValue: '<0,05',
            color: SecondaryNodeColor.GREEN,
          },
        ],
      },
    ],
    resultsData: [resultsAndDefinitions[2].resultsData[0]],
    definitionsData: [
      resultsAndDefinitions[2].definitionsData[0],
      resultsAndDefinitions[2].definitionsData[2],
    ],
  },
  [ConsequenceTitle.LONG_TERM_RELATIONSHIP_ORIENTED]: {
    tables: [
      {
        tableTitle: 'Consequences',
        tableData: [
          {
            author: 'Kim, Yin, Lee, 2020',
            estimate: '0,01',
            pValue: '>0,1',
            color: SecondaryNodeColor.BLUE,
          },
        ],
      },
    ],
    resultsData: [resultsAndDefinitions[3].resultsData[1]],
    definitionsData: [
      resultsAndDefinitions[3].definitionsData[0],
      resultsAndDefinitions[3].definitionsData[2],
    ],
  },
};

type ITargetVariableResultsData = {
  [key in TargetVariableTitle]: IAnalysisNodeData;
};

export const targetVariableResultsData: ITargetVariableResultsData = {
  [TargetVariableTitle.CUSTOMER_KNOWLEDGE_SHARING]: {
    tables: [
      {
        tableTitle: 'Antecedents',
        tableData: [
          ...antecedentResultsData[AntecedentTitle.HELPING_OTHER_CUSTOMERS].tables[0].tableData,
          ...antecedentResultsData[AntecedentTitle.SOCIAL_BOND].tables[0].tableData,
          ...antecedentResultsData[AntecedentTitle.SUBJECTIVE_HAPPINESS].tables[0].tableData,
          ...antecedentResultsData[AntecedentTitle.RECIPROCITY].tables[0].tableData,
        ],
      },
    ],
    ...resultsAndDefinitions[0],
  },
  [TargetVariableTitle.SUGGESTION_SHARING_LIKELIHOOD]: {
    tables: [
      {
        tableTitle: 'Antecedents',
        tableData: [
          ...antecedentResultsData[AntecedentTitle.SELF_BENEFIT].tables[0].tableData,
          ...antecedentResultsData[AntecedentTitle.FIRM_BENEFIT].tables[0].tableData,
          ...antecedentResultsData[AntecedentTitle.OTHER_BENEFIT].tables[0].tableData,
        ],
      },
    ],
    ...resultsAndDefinitions[1],
  },
  [TargetVariableTitle.CUSTOMER_FEEDBACK]: {
    tables: [
      {
        tableTitle: 'Antecedents',
        tableData: [
          ...antecedentResultsData[AntecedentTitle.COMPETITION].tables[0].tableData,
          ...antecedentResultsData[AntecedentTitle.EXPORTS_DESTINATION].tables[0].tableData,
        ],
      },
      {
        tableTitle: 'Consequences',
        tableData: [...consequenceResultsData[ConsequenceTitle.INNOVATION].tables[0].tableData],
      },
    ],
    ...resultsAndDefinitions[2],
  },
  [TargetVariableTitle.PROVIDING_FEEDBACK]: {
    tables: [
      {
        tableTitle: 'Antecedents',
        tableData: [...antecedentResultsData[AntecedentTitle.CORPORATE_IMAGE].tables[0].tableData],
      },
      {
        tableTitle: 'Consequences',
        tableData: [
          ...consequenceResultsData[ConsequenceTitle.LONG_TERM_RELATIONSHIP_ORIENTED].tables[0]
            .tableData,
        ],
      },
    ],
    ...resultsAndDefinitions[3],
  },
};
